import { useState, useEffect } from "react";
import axios from "axios";
import { TrendingUp, Users, UserPlus, Crown, Loader2, Ticket, Send, CheckSquare, Building2, Percent, PlayCircle } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { API } from "@/utils/constants";

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-card border rounded-xl p-4" data-testid={`insight-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <div className="flex items-center gap-2 mb-2">
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${color}`}><Icon className="h-4 w-4" /></div>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
    <p className="text-2xl font-bold">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

export default function AdminInsightsView() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/insights`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || "Failed to load insights"));
  }, []);

  if (error) return <div className="p-6 text-sm text-muted-foreground">{error}</div>;
  if (!data) return <div className="p-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-1 flex items-center gap-2" data-testid="admin-insights-title">
          <TrendingUp className="h-5 w-5 text-primary" />Growth Insights
        </h2>
        <p className="text-sm text-muted-foreground">Signups, activity and conversions across Opero Smart</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={Users} label="Total Users" value={data.total_users} color="bg-sky-500/10 text-sky-600" />
        <StatCard icon={UserPlus} label="Signups" value={data.signups_7d} sub={`${data.signups_30d} in last 30 days`} color="bg-emerald-500/10 text-emerald-600" />
        <StatCard icon={Users} label="Active Users (7d)" value={data.active_users_7d} color="bg-violet-500/10 text-violet-600" />
        <StatCard icon={Percent} label="Conversion Rate" value={`${data.conversion_rate}%`} sub="paid / total users" color="bg-amber-500/10 text-amber-600" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={Crown} label="Active Subscriptions" value={data.active_subscriptions} sub={`${data.active_trials} on trial`} color="bg-orange-500/10 text-orange-600" />
        <StatCard icon={CheckSquare} label="Tasks Created (30d)" value={data.tasks_created_30d} color="bg-teal-500/10 text-teal-600" />
        <StatCard icon={Building2} label="Teams" value={data.teams_total} color="bg-blue-500/10 text-blue-600" />
        <StatCard icon={Ticket} label="Open Tickets" value={data.open_support_tickets} sub={`${data.demo_requests_total} demo requests`} color="bg-red-500/10 text-red-600" />
        <StatCard icon={PlayCircle} label="Demo Video Plays" value={data.video_plays_total ?? 0} sub={`${data.video_plays_7d ?? 0} in last 7 days`} color="bg-fuchsia-500/10 text-fuchsia-600" />
      </div>

      <div className="bg-card border rounded-xl p-5" data-testid="signup-chart">
        <h3 className="font-semibold text-sm mb-4 flex items-center gap-2"><UserPlus className="h-4 w-4 text-primary" />Daily signups — last 30 days</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.signup_timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
              <Tooltip cursor={{ fill: 'hsl(var(--muted))' }} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="signups" fill="#0284c7" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
